import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { B, PD } from "../theme";
import { Constellation } from "../shared/Constellation";
import { AnimatedTitle } from "../shared/AnimatedTitle";
import { PrimaryButton, GhostButton, fieldStyle } from "../shared/ui";
import { birthdayLenses } from "./birthdayLenses";

/* First screen. Says what this is in one breath, then shows it: a birthday
   fills in all four traditions on the spot, next to the Web's own maps.
   The date carries into the birth details, which open on the time step. */

const TRADITIONS = [
  { key: "western", tradition: "Western", sub: "Sun sign", accent: "#68B8C8" },
  { key: "vedic", tradition: "Vedic", sub: "Sun, by the stars", accent: "#D4A574" },
  { key: "chinese", tradition: "Chinese", sub: "Year animal", accent: "#C2625A" },
  { key: "mayan", tradition: "Maya", sub: "Day sign", accent: "#6DA88F" },
];
// The Web's personality maps, with their accents from the Home Web.
const WEB = [
  ["Archetypes", "#8B7BC2"], ["Personality", "#C2A057"], ["Values", "#5E8A6A"],
  ["Behavior", "#D4773B"], ["Belief", "#C27087"], ["Shadows", "#8B4A5C"],
];

const localToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
// "1992-06-14" → { year, month, day } when it's a real date from 1900 to today.
function parseBirthday(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m || iso > localToday()) return null;
  const [year, month, day] = [+m[1], +m[2], +m[3]];
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()) return null;
  return { year, month, day };
}

// Only fetched when the visitor came from a friend's archetype link. If the
// chunk fails to load the card is skipped rather than blanking the landing.
const SharedArchetype = lazy(() => import("./SharedArchetype")
  .then((m) => ({ default: m.SharedArchetype }))
  .catch(() => ({ default: () => null })));

// What Back from the birth details left typed, as the date field's value.
const draftISO = (d) => (d?.month && d?.day && d?.year?.length === 4 ? `${d.year}-${d.month.padStart(2, "0")}-${d.day.padStart(2, "0")}` : "");

export function Landing({ onStart, onSignIn, inviter, sharedArchetype, draft }) {
  const [v, setV] = useState(false);
  useEffect(() => { const t = setTimeout(() => setV(true), 40); return () => clearTimeout(t); }, []);
  const [birthday, setBirthday] = useState(() => draftISO(draft));
  const date = useMemo(() => parseBirthday(birthday), [birthday]);
  const byKey = useMemo(() => {
    let lenses = [];
    try { lenses = date ? birthdayLenses(date) : []; } catch (e) { lenses = []; }
    return Object.fromEntries(lenses.map((l) => [l.key, l]));
  }, [date]);
  const start = () => onStart(date ? { month: String(date.month), day: String(date.day), year: String(date.year) } : null);

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
      padding: "52px 20px 32px", textAlign: "center", background: PD.bg, position: "relative", overflow: "hidden",
      opacity: v ? 1 : 0, transform: v ? "none" : "translateY(14px)", transition: "all 0.8s ease",
    }}>
      <GhostButton onClick={onSignIn} style={{ position: "absolute", top: 16, right: 12, zIndex: 10, color: PD.sub, fontSize: 13 }}>
        Sign in
      </GhostButton>
      <Constellation seed={77} threads={5} accent="#9590A8" luminous={true} style={{ opacity: 0.4 }} />

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 520 }}>
        <div style={{ marginBottom: 4 }}><AnimatedTitle /></div>
        <p style={{ ...B, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", color: PD.sub, margin: "0 0 24px", fontWeight: 500, opacity: 0.8 }}>
          Get Lost and Found
        </p>

        {sharedArchetype && (
          <Suspense fallback={null}><SharedArchetype archetypeKey={sharedArchetype} /></Suspense>
        )}
        {inviter && (
          <p style={{ ...B, fontSize: 15, color: PD.text, lineHeight: 1.6, margin: "-20px auto 26px", maxWidth: 400 }}>
            {inviter} sent you this link. Read your chart, save it, and you two are connected.
          </p>
        )}
        {/* With a friend's archetype card above, this line is dropped so
            "Read my chart" stays on the first screen of a small phone. */}
        {sharedArchetype ? <div style={{ height: 8 }} /> : (
          <p style={{ ...B, fontSize: 16, color: PD.text, lineHeight: 1.55, margin: "0 auto 18px", maxWidth: 360 }}>
            Your stars and your personality in one place, and how they line up with the people you care about.
          </p>
        )}

        <label htmlFor="landing-birthday" style={{ ...B, display: "block", fontSize: 14, color: PD.text, marginBottom: 8 }}>
          Your birthday
        </label>
        <input
          id="landing-birthday" type="date" value={birthday} min="1900-01-01" max={localToday()}
          onChange={(e) => setBirthday(e.target.value)}
          style={{ ...fieldStyle, display: "block", maxWidth: 320, margin: "0 auto 16px", textAlign: "center", fontSize: 16 }}
        />

        {/* The four traditions, filled in from the date as it's picked. */}
        <p style={{ ...B, fontSize: 13, color: PD.sub, margin: "0 0 8px" }}>Your sky</p>
        <div aria-live="polite" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, maxWidth: 400, margin: "0 auto 16px", textAlign: "left" }}>
          {TRADITIONS.map((t) => {
            const l = byKey[t.key];
            return (
              <div key={t.key} style={{
                padding: "9px 12px 10px", borderRadius: 12, background: "rgba(20,22,40,0.5)",
              }}>
                <div aria-hidden="true" style={{ width: 22, height: 2, borderRadius: 1, background: t.accent, opacity: l ? 1 : 0.4, transition: "opacity 0.4s ease", marginBottom: 6 }} />
                <div style={{ ...B, fontSize: 13, color: t.accent, fontWeight: 500 }}>{t.tradition}</div>
                <div style={{ ...B, fontSize: 16, color: l ? PD.text : PD.dim, fontWeight: 500, marginTop: 2, overflowWrap: "break-word" }}>
                  {l ? l.value : "· · ·"}
                </div>
                <div style={{ ...B, fontSize: 13, color: PD.sub, marginTop: 1 }}>{l ? l.sub : t.sub}</div>
              </div>
            );
          })}
        </div>

        {/* The Web's maps, as plain labels under a heading that matches
            "Your sky": they open after signup. */}
        <p style={{ ...B, fontSize: 13, color: PD.sub, margin: "0 0 8px" }}>Your personality, from a few quick questions</p>
        <ul style={{ listStyle: "none", padding: 0, margin: `0 auto ${sharedArchetype ? 20 : 24}px`, maxWidth: 400, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 14px" }}>
          {WEB.map(([name, accent]) => (
            <li key={name} style={{ ...B, fontSize: 15, color: PD.text, display: "flex", alignItems: "center", gap: 6 }}>
              <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 3, background: accent, flexShrink: 0 }} />
              {name}
            </li>
          ))}
        </ul>

        <PrimaryButton onClick={start}>Read my chart</PrimaryButton>
        <p style={{ ...B, fontSize: 13, color: PD.sub, opacity: 0.9, marginTop: 14 }}>
          Free. No account needed to start.
        </p>
        <p style={{ ...B, fontSize: 13, color: PD.sub, opacity: 0.8, marginTop: 28 }}>
          <a href="/privacy" style={{ color: "inherit", display: "inline-block", padding: "14px 10px", minHeight: 44, boxSizing: "border-box" }}>Privacy</a>
          <a href="/terms" style={{ color: "inherit", display: "inline-block", padding: "14px 10px", minHeight: 44, boxSizing: "border-box" }}>Terms</a>
        </p>
      </div>
    </div>
  );
}
